import React, { useMemo, useState } from 'react'
import {
    FunnelChart,
    Funnel,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    Cell,
} from 'recharts'
import { AnalyticsChartWrapper } from './AnalyticsChartWrapper'
import {
    CACBenchmarkLegend,
    FunnelLegendRow,
    FunnelStageKey,
    FUNNEL_STAGE_SUBTITLES,
    getMarketReferenceRate,
} from './CACBenchmarkLegend'

interface FunnelStep {
    name: string
    value: number
    fill: string
}

interface NormalizedFunnelStep extends FunnelStep {
    realValue: number
    conversionRate: string | null
}

interface FunnelLabelProps {
    x?: number | string
    y?: number | string
    width?: number | string
    height?: number | string
    value?: number
    realValue?: number
    name?: string
    conversionRate?: string | null
    payload?: Partial<NormalizedFunnelStep>
    row?: NormalizedFunnelStep
    index?: number
}

interface FunnelTooltipProps {
    active?: boolean
    payload?: Array<{
        payload: FunnelStep & { realValue?: number }
    }>
}

interface AnalyticsFunnelChartProps {
    data: FunnelStep[]
    transitioning?: boolean
    hasToggles?: boolean
    marginModifier?: number
    onPointClick?: (payload: {
        chartType: string
        labelKey: string
        labelValue: string | number
        row: Record<string, unknown>
    }) => void
}

const FUNNEL_COLORS = ['#3498db', '#27ae60', '#e67e22', '#e74c3c']
const REF_COLORS = ['#90a4ae', '#78909c', '#607d8b', '#546e7a']
const FAUX_FUNNEL_TOP = 100
const FAUX_FUNNEL_BOTTOM = 20

const FALLBACK_STAGE_KEYS: FunnelStageKey[] = ['visit', 'registration', 'meeting', 'sale']

const CustomTooltip = ({ active, payload }: FunnelTooltipProps) => {
    if (!active || !payload || !payload.length) return null
    const item = payload[0].payload
    const display = item.realValue ?? item.value
    return (
        <div className="analytics-tooltip">
            <p className="analytics-tooltip-label">{item.name}</p>
            <p className="analytics-tooltip-value">{display.toLocaleString()}</p>
        </div>
    )
}

const CustomLabel = (props: FunnelLabelProps) => {
    const {
        x = 0,
        y = 0,
        width = 0,
        height = 0,
        value,
        realValue,
        name,
        conversionRate,
        payload,
        row,
    } = props
    const source = row || payload || {}
    const display = source.realValue ?? realValue ?? value
    const labelName = source.name ?? name
    const labelRate = source.conversionRate ?? conversionRate
    if (!display && display !== 0) return null
    const cx = Number(x) + (Number(width) / 2)
    const cy = Number(y) + (Number(height) / 2)
    const statLabel = `${Number(display).toLocaleString()} (${String(labelRate || '—')})`
    return (
        <g>
            <text
                x={cx}
                y={cy - 10}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#fff"
                fontSize={15}
                fontWeight={600}
                paintOrder="stroke"
                stroke="rgba(0,0,0,0.45)"
                strokeWidth={3}
                strokeLinejoin="round"
            >
                {labelName}
            </text>
            <text
                x={cx}
                y={cy + 12}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#fff"
                fontSize={18}
                fontWeight={700}
                paintOrder="stroke"
                stroke="rgba(0,0,0,0.45)"
                strokeWidth={3}
                strokeLinejoin="round"
            >
                {statLabel}
            </text>
        </g>
    )
}

function getStageKeyFromName(name: string, index: number): FunnelStageKey {
    const lower = (name || '').toLowerCase()
    if (lower.includes('visit')) return 'visit'
    if (lower.includes('register') || lower.includes('lead')) return 'registration'
    if (lower.includes('meeting') || lower.includes('appointment')) return 'meeting'
    if (lower.includes('sale') || lower.includes('deal') || lower.includes('close')) return 'sale'
    return FALLBACK_STAGE_KEYS[index] || 'sale'
}

function getFauxFunnelValue(index: number, steps: number): number {
    if (steps <= 1) return FAUX_FUNNEL_TOP
    const position = (steps - 1 - index) / (steps - 1)
    const shapedRatio = (
        FAUX_FUNNEL_BOTTOM
        + ((FAUX_FUNNEL_TOP - FAUX_FUNNEL_BOTTOM) * (position ** 2))
    )
    return Math.round(shapedRatio)
}

function parseMarketReferenceRate(rate: string): number | null {
    if (!rate || rate === '—') return null
    const numericRate = Number(rate.replace('%', ''))
    return Number.isFinite(numericRate) ? numericRate / 100 : null
}

function isValidFunnelBase(value: number): boolean {
    return Number.isFinite(value) && value > 0
}

function getDensityClass(marginModifier: number): string {
    if (marginModifier <= 0.4) return 'density-quarter'
    if (marginModifier <= 0.75) return 'density-half'
    return 'density-full'
}

export function AnalyticsFunnelChart(props: AnalyticsFunnelChartProps) {
    const {
        data,
        transitioning,
        hasToggles,
        marginModifier = 1.0,
        onPointClick,
    } = props
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const [activeIndex, setActiveIndex] = useState<number | null>(null)

    const densityClass = getDensityClass(marginModifier)

    const focusedIndex = hoveredIndex ?? activeIndex

    const coloredData = useMemo(() => data.map((step, i) => ({
        ...step,
        fill: step.fill || FUNNEL_COLORS[i % FUNNEL_COLORS.length],
    })), [data])

    const normalizedData = useMemo<NormalizedFunnelStep[]>(() => {
        const steps = coloredData.length
        if (steps === 0) return []
        // Fixed faux funnel silhouette with a non-zero bottom.
        return coloredData.map((item, i) => ({
            ...item,
            realValue: item.value,
            value: getFauxFunnelValue(i, steps),
            conversionRate: i === 0
                ? null
                : (() => {
                    const prev = coloredData[i - 1].value
                    const pct = prev > 0 ? (item.value / prev) * 100 : 0
                    return `${Math.round(pct)}%`
                })(),
        }))
    }, [coloredData])

    const refNormalizedData = useMemo<NormalizedFunnelStep[]>(() => {
        const steps = coloredData.length
        if (steps === 0) return []

        const stageKeys = coloredData.map((item, i) => getStageKeyFromName(item.name, i))
        const firstValidIndex = coloredData.findIndex(
            (item) => isValidFunnelBase(item.value),
        )
        const rawReferenceValues = new Array<number>(steps).fill(0)

        if (firstValidIndex >= 0) {
            rawReferenceValues[firstValidIndex] = coloredData[firstValidIndex].value

            for (let i = firstValidIndex + 1; i < steps; i += 1) {
                const referenceRate = parseMarketReferenceRate(getMarketReferenceRate(stageKeys[i]))
                rawReferenceValues[i] = referenceRate == null
                    ? rawReferenceValues[i - 1]
                    : rawReferenceValues[i - 1] * referenceRate
            }
        }

        return coloredData.map((item, i) => ({
            name: item.name,
            realValue: Math.round(rawReferenceValues[i]),
            value: getFauxFunnelValue(i, steps),
            conversionRate: i === 0
                ? null
                : getMarketReferenceRate(stageKeys[i]),
            fill: REF_COLORS[i % REF_COLORS.length],
        }))
    }, [coloredData])

    const annotations: { pct: string | null }[] = coloredData.map((step, i) => {
        if (i === 0) return { pct: null }
        const prev = coloredData[i - 1].value
        const pct = prev > 0 ? (step.value / prev) * 100 : 0
        return { pct: `${Math.round(pct)}%` }
    })

    const legendRows: FunnelLegendRow[] = useMemo(() => coloredData.map((step, i) => {
        const key = getStageKeyFromName(step.name, i)
        return {
            key,
            title: step.name,
            subtitle: FUNNEL_STAGE_SUBTITLES[key],
            count: step.value,
            ourRate: annotations[i].pct || '—',
            marketRate: getMarketReferenceRate(key),
            color: step.fill,
        }
    }), [annotations, coloredData])

    const handleSectionClick = (index: number) => {
        if (!onPointClick || !coloredData[index]) {
            return
        }

        const step = coloredData[index]
        const stage = getStageKeyFromName(step.name, index)

        setActiveIndex(index)

        onPointClick({
            chartType: 'funnel',
            labelKey: 'funnelStage',
            labelValue: stage,
            row: {
                ...step,
                stage,
                stageLabel: step.name,
                stageIndex: index,
            },
        })
    }

    return (
        <AnalyticsChartWrapper
            transitioning={transitioning}
            hasToggles={hasToggles}
            marginModifier={marginModifier}
        >
            <div className={`analytics-funnel-chart ${densityClass}${onPointClick ? ' drillable' : ''}`}>
                <div className="funnel-columns">

                    {/* Column 1: Legend */}
                    <div className="funnel-legend-col">
                        <CACBenchmarkLegend
                            title="Sales Funnel"
                            rows={legendRows}
                            showBadges
                            dense={densityClass !== 'density-full'}
                        />
                    </div>

                    {/* Column 2: Your funnel */}
                    <div className="funnel-main-col">
                        <div className="funnel-col-title">Your Funnel</div>
                        <div className="analytics-funnel-chart-container">
                            <ResponsiveContainer width="100%" height="100%">
                                <FunnelChart>
                                    <Tooltip content={<CustomTooltip />} />
                                    <Funnel
                                        dataKey="value"
                                        data={normalizedData}
                                        isAnimationActive={false}
                                        labelLine={false}
                                        lastShapeType="rectangle"
                                    >
                                        {normalizedData.map((entry, index) => {
                                            const isFocused = focusedIndex === index
                                            const isDimmed = focusedIndex !== null && !isFocused
                                            return (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={entry.fill}
                                                    stroke="rgba(255, 255, 255, 0.55)"
                                                    strokeWidth={1}
                                                    className={`analytics-funnel-stage-cell${isFocused ? ' is-focused' : ''}${isDimmed ? ' is-dimmed' : ''}`}
                                                    onMouseEnter={() => setHoveredIndex(index)}
                                                    onMouseLeave={() => setHoveredIndex(null)}
                                                    onClick={() => handleSectionClick(index)}
                                                />
                                            )
                                        })}
                                        <LabelList
                                            dataKey="value"
                                            content={(p: FunnelLabelProps) => (
                                                <CustomLabel
                                                    {...p}
                                                    row={typeof p.index === 'number'
                                                        ? normalizedData[p.index]
                                                        : undefined}
                                                />
                                            )}
                                        />
                                    </Funnel>
                                </FunnelChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Column 3: Market Reference */}
                    <div className="funnel-ref-col">
                        <div className="funnel-col-title">Market Reference</div>
                        <div className="analytics-funnel-chart-container">
                            <ResponsiveContainer width="100%" height="100%">
                                <FunnelChart>
                                    <Funnel
                                        dataKey="value"
                                        data={refNormalizedData}
                                        isAnimationActive={false}
                                        labelLine={false}
                                        lastShapeType="rectangle"
                                    >
                                        {refNormalizedData.map((_, i) => (
                                            <Cell key={`ref-cell-${i}`} fill={REF_COLORS[i % REF_COLORS.length]} />
                                        ))}
                                        <LabelList
                                            dataKey="value"
                                            content={(p: FunnelLabelProps) => (
                                                <CustomLabel
                                                    {...p}
                                                    row={typeof p.index === 'number'
                                                        ? refNormalizedData[p.index]
                                                        : undefined}
                                                />
                                            )}
                                        />
                                    </Funnel>
                                </FunnelChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                </div>
            </div>
        </AnalyticsChartWrapper>
    )
}
